import { roleAtLeast, type MembershipRole } from '@ai-sales/shared'
import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { ErrorPanel } from '../components/error-panel.js'
import { Spinner } from '../components/spinner.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'
import { useSession } from './session-context.js'

/**
 * Закрытый маршрут.
 *
 * Важная оговорка, которую стоит держать в голове всем, кто будет это
 * дописывать: проверка здесь — про удобство, а не про безопасность.
 * Настоящая проверка живёт на сервере в AuthGuard и WorkspaceGuard; здесь
 * мы лишь избавляем человека от экрана, который всё равно ответит отказом.
 * Поэтому добавлять сюда логику «что можно показывать» без соответствующей
 * проверки на сервере — ошибка, даже если экран выглядит защищённым.
 */
export function RequireAuth() {
  const { status, error, refetch } = useSession()
  const location = useLocation()

  if (status === 'loading') {
    return <FullScreenLoader />
  }

  if (error) {
    /**
     * Сеть или сервер, а не отсутствие сессии. Перебрасывать на вход нельзя:
     * человек введёт пароль, получит ту же ошибку и решит, что забыл пароль.
     */
    return (
      <div className="mx-auto max-w-lg p-8">
        <ErrorPanel error={error} onRetry={refetch} />
      </div>
    )
  }

  if (status === 'anonymous') {
    // Запоминаем, куда человек шёл, чтобы после входа вернуть его туда же
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  return <Outlet />
}

/**
 * Маршрут, требующий роли не ниже заданной в текущем воркспейсе.
 * Отказ показывается как объяснение, а не как пустая страница: человек
 * должен понять, что дело в роли, и знать, к кому идти.
 */
export function RequireRole({ role, children }: { role: MembershipRole; children?: ReactNode }) {
  const workspace = useCurrentWorkspace()

  if (!roleAtLeast(workspace.role, role)) {
    return (
      <div className="rounded-md border border-line bg-surface px-6 py-10 text-center">
        <p className="text-sm font-medium text-ink">Раздел недоступен с вашей ролью</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted">
          Нужна роль не ниже «{ROLE_LABELS[role]}», у вас — «{ROLE_LABELS[workspace.role]}».
          Права меняет владелец воркспейса в разделе «Участники».
        </p>
      </div>
    )
  }

  return <>{children ?? <Outlet />}</>
}

export const ROLE_LABELS: Record<MembershipRole, string> = {
  owner: 'владелец',
  admin: 'администратор',
  operator: 'оператор',
  viewer: 'наблюдатель',
}

export function FullScreenLoader() {
  return (
    <div className="flex min-h-dvh items-center justify-center gap-3 text-muted">
      <Spinner size={18} />
      <span className="text-sm">Загружаем LeadPilot…</span>
    </div>
  )
}
